/**
 * Calendar Segmentation Utilities - Eastern Time (America/New_York)
 * 
 * Handles calendar pill rendering for multi-day stays with correct:
 * - Date-only parsing (no timezone shifts)
 * - Overlap detection (cross-month stays)
 * - Week segmentation (pills split at week boundaries)
 * - Exclusive checkout semantics ([checkin, checkout) - checkout day not occupied)
 */

import { normalizeToYmd } from './dateTimeET.js';

/**
 * Resolve canonical check-in and check-out dates from a booking object
 * Handles multiple field name aliases
 * @param {Object} booking - Booking object with date fields
 * @returns {Object} {checkinYmd: string|null, checkoutYmd: string|null}
 */
export function resolveCanonicalDates(booking) {
  if (!booking) {
    return { checkinYmd: null, checkoutYmd: null };
  }

  // Try multiple field name aliases (same priority as BookingDetailModal)
  const checkinValue = 
    booking.check_in_date || booking.checkin_date || 
    booking.arrivalDate || booking.start_date;

  const checkoutValue = 
    booking.check_out_date || booking.checkout_date ||
    booking.departureDate || booking.end_date;

  // Normalize to YYYY-MM-DD (handles ISO timestamps, Date objects, date-only strings)
  const checkinYmd = normalizeToYmd(checkinValue);
  const checkoutYmd = normalizeToYmd(checkoutValue);

  return { checkinYmd, checkoutYmd };
}

/**
 * Parse YYYY-MM-DD date string as ET date-only (no timezone conversion)
 * Returns a Date object representing the calendar date in ET
 * @param {string} yyyyMmDd - Date string in YYYY-MM-DD format
 * @returns {Date|null} - Date object or null if invalid
 */
export function parseDateOnlyET(yyyyMmDd) {
  if (!yyyyMmDd || typeof yyyyMmDd !== 'string') {
    return null;
  }

  const dateMatch = yyyyMmDd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) {
    return null;
  }

  const [, yearStr, monthStr, dayStr] = dateMatch;
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10) - 1; // JavaScript months are 0-indexed
  const day = parseInt(dayStr, 10);

  // Validate
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return null;
  }

  // Create UTC date at noon to avoid timezone boundary issues
  // This represents the calendar date in ET without shifting
  return new Date(Date.UTC(year, month, day, 12, 0, 0));
}

/**
 * Calculate if a booking overlaps a month and determine display boundaries
 * Overlap rule: booking overlaps if (checkin <= monthEnd) AND (checkout >= monthStart)
 * This ensures reservations spanning month boundaries show pills in both months
 * @param {string} checkinYmd - Check-in date in YYYY-MM-DD format
 * @param {string} checkoutYmd - Check-out date in YYYY-MM-DD format (exclusive - checkout day not occupied)
 * @param {string} monthStartYmd - Month start date in YYYY-MM-DD format
 * @param {string} monthEndYmd - Month end date in YYYY-MM-DD format (inclusive)
 * @returns {Object} {overlaps: boolean, displayStartYmd: string|null, displayEndYmd: string|null}
 */
export function calculateMonthOverlap(checkinYmd, checkoutYmd, monthStartYmd, monthEndYmd) {
  if (!checkinYmd || !checkoutYmd || !monthStartYmd || !monthEndYmd) {
    return { overlaps: false, displayStartYmd: null, displayEndYmd: null };
  }

  // Use string comparison for date-only values (no timezone issues)
  // Overlap: checkin <= monthEnd AND checkout >= monthStart
  // This ensures reservations starting/ending on month boundaries are included in both months
  const overlaps = checkinYmd <= monthEndYmd && checkoutYmd >= monthStartYmd;

  if (!overlaps) {
    return { overlaps: false, displayStartYmd: null, displayEndYmd: null };
  }

  // Clip to month boundaries for display
  // displayStart = max(checkin, monthStart)
  // displayEnd = min(checkout, monthEnd)
  // Note: checkout is inclusive for rendering (pill extends through checkout day)
  const displayStartYmd = checkinYmd > monthStartYmd ? checkinYmd : monthStartYmd;
  
  // For displayEnd, we want checkout day (inclusive), clipped to month
  // If checkout is within month, displayEnd = checkout
  // If checkout is after month, displayEnd = monthEnd
  let displayEndYmd;
  if (checkoutYmd <= monthEndYmd) {
    // Checkout is within or at end of month - include checkout day
    displayEndYmd = checkoutYmd;
  } else {
    // Checkout is after month - last visible day is monthEnd
    displayEndYmd = monthEndYmd;
  }

  // Ensure displayEndYmd >= displayStartYmd
  // When displayStartYmd === displayEndYmd (e.g., single day on month boundary), this is valid
  if (displayEndYmd < displayStartYmd) {
    return { overlaps: false, displayStartYmd: null, displayEndYmd: null };
  }

  // At this point, displayEndYmd >= displayStartYmd, so we have a valid overlap
  // Even if displayStartYmd === displayEndYmd (single-day segment), this is correct
  return { overlaps: true, displayStartYmd, displayEndYmd };
}

/**
 * Subtract one day from a YYYY-MM-DD date string
 * @param {string} yyyyMmDd - Date string in YYYY-MM-DD format
 * @returns {string} - Previous day in YYYY-MM-DD format
 */
export function subtractOneDay(yyyyMmDd) {
  const date = parseDateOnlyET(yyyyMmDd);
  if (!date) return yyyyMmDd;

  // Subtract one day
  date.setUTCDate(date.getUTCDate() - 1);

  // Format back to YYYY-MM-DD
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Format YYYY-MM-DD from day of month
 * @param {number} dayOfMonth - Day of month (1-based)
 * @param {Date} monthStartDate - Month start date
 * @returns {string} - YYYY-MM-DD format
 */
function formatYmdFromDayOfMonth(dayOfMonth, monthStartDate) {
  const date = new Date(monthStartDate);
  date.setUTCDate(date.getUTCDate() + dayOfMonth - 1);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Calculate week segments for a booking span within a month
 * Splits the span at week boundaries (Sunday to Saturday)
 * @param {string} displayStartYmd - Display start date (YYYY-MM-DD)
 * @param {string} displayEndYmd - Display end date (YYYY-MM-DD, inclusive - includes checkout day)
 * @param {string} monthStartYmd - Month start date (YYYY-MM-DD)
 * @param {number} firstDayOfWeek - Day of week for month start (0=Sunday, 6=Saturday)
 * @returns {Array} Array of week segment objects: {weekNumber, gridStartIndex, gridEndIndex, spanDays, dayOfMonth}
 */
export function calculateWeekSegments(displayStartYmd, displayEndYmd, monthStartYmd, firstDayOfWeek) {
  if (!displayStartYmd || !displayEndYmd || !monthStartYmd) {
    return [];
  }

  const segments = [];
  const startDate = parseDateOnlyET(displayStartYmd);
  const endDate = parseDateOnlyET(displayEndYmd);
  const monthStartDate = parseDateOnlyET(monthStartYmd);

  if (!startDate || !endDate || !monthStartDate) {
    return [];
  }

  // Calculate day of month for start and end
  const startDayOfMonth = Math.floor((startDate.getTime() - monthStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const endDayOfMonth = Math.floor((endDate.getTime() - monthStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

  // Calculate grid indices (including padding days before month starts)
  const startGridIndex = startDayOfMonth - 1 + firstDayOfWeek;
  const endGridIndex = endDayOfMonth - 1 + firstDayOfWeek;

  // Calculate week numbers
  const startWeekNumber = Math.floor(startGridIndex / 7);
  const endWeekNumber = Math.floor(endGridIndex / 7);
  
  // If span is within a single week, return single segment
  if (startWeekNumber === endWeekNumber) {
    const spanDays = endDayOfMonth - startDayOfMonth + 1; // Inclusive: includes both start and end days
    // Ensure spanDays is at least 1 (safeguard for single-day segments on month boundaries)
    if (spanDays <= 0) {
      // Edge case: if calculation produces 0 or negative, ensure at least 1 day
      return [{
        weekNumber: startWeekNumber,
        gridStartIndex: startGridIndex,
        gridEndIndex: endGridIndex,
        spanDays: 1,
        dayOfMonth: startDayOfMonth,
        dayOfWeek: startGridIndex % 7,
      }];
    }
    return [{
      weekNumber: startWeekNumber,
      gridStartIndex: startGridIndex,
      gridEndIndex: endGridIndex,
      spanDays,
      dayOfMonth: startDayOfMonth,
      dayOfWeek: startGridIndex % 7,
    }];
  }

  // Span crosses week boundaries - split into multiple segments
  let currentDate = new Date(startDate);
  let currentDayOfMonth = startDayOfMonth;
  let currentGridIndex = startGridIndex;

  while (currentDate <= endDate) {
    // Find end of current week (Saturday)
    const currentDayOfWeek = currentDate.getUTCDay();
    const daysUntilWeekEnd = 6 - currentDayOfWeek;
    
    // Calculate segment end (either end of week or end of span, whichever comes first)
    const weekEndDate = new Date(currentDate);
    weekEndDate.setUTCDate(weekEndDate.getUTCDate() + daysUntilWeekEnd);
    
    const segmentEndDate = weekEndDate < endDate ? weekEndDate : endDate;
    const segmentEndDayOfMonth = Math.floor((segmentEndDate.getTime() - monthStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const segmentEndGridIndex = segmentEndDayOfMonth - 1 + firstDayOfWeek;

    // Calculate span days for this segment (inclusive)
    const segmentSpanDays = segmentEndDayOfMonth - currentDayOfMonth + 1;

    // Calculate week number
    const weekNumber = Math.floor(currentGridIndex / 7);

    segments.push({
      weekNumber,
      gridStartIndex: currentGridIndex,
      gridEndIndex: segmentEndGridIndex,
      spanDays: segmentSpanDays,
      dayOfMonth: currentDayOfMonth,
      dayOfWeek: currentGridIndex % 7,
    });

    // Move to start of next week (Sunday)
    currentDate = new Date(segmentEndDate);
    currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    currentDayOfMonth = Math.floor((currentDate.getTime() - monthStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    currentGridIndex = currentDayOfMonth - 1 + firstDayOfWeek;
  }

  return segments;
}
